"use client";

import { useLinkStatus } from "next/link";
import React from "react";
import { cn } from "../../helpers/classNames";
import { formatTemperature } from "../../helpers/formatters/temperature";
import type { LocationSummary } from "../../network/matlocq/types/locationSummary";
import { SpinningSun } from "../SpinningSun";
import { WeatherIcon } from "../WeatherIcon";
import styles from "./styles.module.scss";

type ItemInnerProps = {
    favorite: LocationSummary;
    totalAmount: number;
    // TODO isLoading is used for compatibility with <a>, can be dropped, when everything is NextLink
    isLoading?: boolean;
};

export const ItemInner: React.FC<ItemInnerProps> = ({
    favorite,
    totalAmount,
    isLoading,
}) => {
    const { pending } = useLinkStatus();

    if (pending || isLoading) {
        return <SpinningSun size={35} />;
    }

    const warningText = favorite.warnings.highestWarnGroupText;
    return (
        <div
            className={cn(styles.grid, totalAmount === 1 && styles.gridSingle)}
        >
            <div className={styles.iconWrapper}>
                <WeatherIcon
                    // cut out path, as it is set in WeatherIcon component, is needed in data for old web-app
                    url={
                        favorite.forecastSummary.items[0]?.weatherAggregated
                            ?.iconUrl ?? null
                    }
                    alt={
                        favorite.forecastSummary.items[0]?.weatherAggregated
                            ?.text ?? ""
                    }
                    title={
                        favorite.forecastSummary.items[0]?.weatherAggregated
                            ?.text ?? ""
                    }
                    imgTagSize={{
                        width: totalAmount === 1 ? 70 : 44,
                        height: totalAmount === 1 ? 50 : 38,
                    }}
                    lazyloadImage={false}
                />
            </div>
            <div className={styles.locationName}>
                {favorite.location?.name ?? ""}
            </div>
            {totalAmount === 1 && (
                <>
                    <div
                        className={cn(
                            styles.weatherState,
                            !warningText && styles.weatherStateMultiline
                        )}
                    >
                        {favorite.forecastSummary.items[0]?.weatherAggregated
                            .text ?? ""}
                    </div>
                    {warningText ? (
                        <div className={styles.warnings}>{warningText}</div>
                    ) : null}
                </>
            )}
            <div
                className={
                    totalAmount === 1
                        ? styles.temperatureWrapperSingle
                        : styles.temperatureWrapperMultiple
                }
            >
                <span
                    className={cn(
                        styles.temperatureMax,
                        totalAmount === 1 && styles.temperatureMaxSingle
                    )}
                >
                    {formatTemperature(
                        favorite.forecastSummary.items[0]?.temperature?.max
                    )}
                </span>
                <span className={styles.separator}>/</span>
                <span className={styles.temperatureMin}>
                    {formatTemperature(
                        favorite.forecastSummary.items[0]?.temperature?.min
                    )}
                </span>
            </div>
        </div>
    );
};
